// Shared "merge two patient records" flow, reused from both the Verify
// stage (duplicate review, migrated) and Patient Records (still legacy
// .jsx). Doesn't touch any data itself — it only files a
// patient_merge_requests row; an Admin has to approve it before the actual
// re-pointing runs (see backend/utils/patientMerge.js).
//
// Kept in components/ (not moved into features/registration/) since it is
// genuinely shared with a still-legacy screen — same reasoning as
// ConsultationChat.jsx staying put. Converting the file to TSX and its
// modal shell to Dialog is safe from a legacy .jsx caller: allowJs does not
// type-check PatientRecords.jsx, and the exported component keeps its
// original prop names and runtime shape unchanged.
import { useState } from 'react';
import { authFetch, createMergeRequest } from '../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/Dialog';
import { Button } from './ui/Button';
import { Input, Textarea } from './ui/Input';

interface MergePatient {
  id: number;
  name: string;
  uhid: string;
  gender: string;
  age: number | null;
  mobile: string;
}

function PatientCard({
  patient,
  selected,
  onSelect,
  role,
}: {
  patient: MergePatient;
  selected: boolean;
  onSelect: () => void;
  role: string;
}) {
  return (
    <div
      onClick={onSelect}
      className="flex-1 cursor-pointer rounded-[var(--radius)] p-3"
      style={{ border: `2px solid ${selected ? 'var(--teal)' : '#E5E7EB'}`, background: selected ? 'rgba(11,95,214,.06)' : '#fff' }}
    >
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.5px]" style={{ color: selected ? 'var(--teal)' : '#9CA3AF' }}>
        {role} {selected && '✓'}
      </div>
      <div className="text-sm font-bold">{patient.name}</div>
      <div className="mt-0.5 text-xs text-ink-muted">{patient.uhid} · {patient.gender}, {patient.age ?? '—'}y · 📱 {patient.mobile}</div>
    </div>
  );
}

export interface MergePatientsModalProps {
  patientA: MergePatient;
  onClose: () => void;
  onSubmitted?: () => void;
}

export default function MergePatientsModal({ patientA, onClose, onSubmitted }: MergePatientsModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MergePatient[]>([]);
  const [searching, setSearching] = useState(false);
  const [patientB, setPatientB] = useState<MergePatient | null>(null);
  const [survivorId, setSurvivorId] = useState(patientA.id);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await authFetch(`/api/patients/search?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data.filter((p: MergePatient) => p.id !== patientA.id) : []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  const submit = async () => {
    if (!patientB) return;
    setSubmitting(true);
    setError('');
    const loserId = survivorId === patientA.id ? patientB.id : patientA.id;
    try {
      await createMergeRequest({ survivor_id: survivorId, loser_id: loserId, reason: reason.trim() || null });
      setDone(true);
    } catch (e: any) {
      setError(e.message);
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[600px]">
        <DialogHeader>
          <DialogTitle>🔗 Merge with another patient record</DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="p-5 text-center">
            <div className="mb-2 text-3xl">⏳</div>
            <div className="mb-1 font-bold">Merge request submitted</div>
            <div className="mb-4 text-[13px] text-ink-muted">
              An Admin needs to approve this before the records are actually combined. Nothing has changed yet.
            </div>
            <Button onClick={() => (onSubmitted ? onSubmitted() : onClose())}>Done</Button>
          </div>
        ) : (
          <>
            <div className="text-[13px] leading-[1.6] text-ink-muted">
              Find the other record to merge, choose which one should survive, then submit for approval — visits, documents, bills
              and history all move onto the surviving record once approved.
            </div>

            {!patientB ? (
              <div>
                <label className="lbl">Search for the other patient (UHID / name / mobile)</label>
                <div className="flex gap-2">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && search()}
                    placeholder="e.g. UH2501 or Ananya or 9876…"
                  />
                  <Button variant="ghost" onClick={search} className="shrink-0">
                    {searching ? '…' : 'Search'}
                  </Button>
                </div>
                <div className="mt-2.5 flex flex-col gap-1.5">
                  {results.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => { setPatientB(p); setSurvivorId(patientA.id); }}
                      className="cursor-pointer rounded-[var(--radius)] border border-line p-2.5"
                    >
                      <div className="text-[13px] font-semibold">{p.name}</div>
                      <div className="text-xs text-ink-muted">{p.uhid} · {p.gender}, {p.age ?? '—'}y · 📱 {p.mobile}</div>
                    </div>
                  ))}
                  {results.length === 0 && query && !searching && (
                    <div className="py-2 text-xs text-ink-muted/80">No matches yet — try searching above.</div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <label className="lbl">Which record should survive?</label>
                <div className="mb-3.5 flex gap-2.5">
                  <PatientCard patient={patientA} selected={survivorId === patientA.id} onSelect={() => setSurvivorId(patientA.id)} role="Option A" />
                  <PatientCard patient={patientB} selected={survivorId === patientB.id} onSelect={() => setSurvivorId(patientB.id)} role="Option B" />
                </div>
                <Button variant="ghost" size="sm" className="mb-3.5" onClick={() => setPatientB(null)}>
                  ← Search a different record
                </Button>

                <label className="lbl">Reason (optional)</label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Confirmed same patient — matched by mobile number and DOB"
                  className="mb-3.5 h-[60px]"
                />

                {error && <div className="mb-3.5 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

                <Button disabled={submitting} onClick={submit}>
                  {submitting ? 'Submitting…' : 'Submit for admin approval →'}
                </Button>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
